import { Dialog } from '@cimplico/components';
import { toastNotification } from '../../components/toast/Toast';
import { useReviewWorksheets } from '../../queries/use-review-worksheets';
import plural from '../../utilities/plural';

type WorksheetReviewConfirmProps = {
    worksheetIds: string[];
    onClose: () => void;
    onReview: () => void;
    onError: (errorMessages: { [id: string]: string }) => void;
};

function WorksheetReviewConfirm({ worksheetIds, onClose, onReview, onError }: WorksheetReviewConfirmProps): JSX.Element {
    const descriptionStart = `Do you want to review ${plural(worksheetIds.length, 'this worksheet', `${worksheetIds.length} worksheets`)}?`;

    const reviewWorksheetsQuery = useReviewWorksheets({});

    function review(): void {
        reviewWorksheetsQuery.mutate(
            worksheetIds.map((id) => ({ id })),
            {
                onSuccess: () => {
                    onReview();
                    toastNotification(
                        'success',
                        `${worksheetIds.length} ${plural(worksheetIds.length, 'worksheet', 'worksheets')} marked as reviewed!`
                    );
                },

                onError: (err) => {
                    const results = err.response?.data?.results ?? [];

                    if (worksheetIds.length === 1) {
                        toastNotification('error', 'Unable to review this worksheet!', results[0].error?.fields?.errorMessage);
                        onClose();
                        return;
                    }

                    const errors = results.filter((r: { statusCode: number }) => r.statusCode >= 400);
                    const success = results.filter((r: { statusCode: number }) => r.statusCode < 400);

                    if (success.length > 0) {
                        toastNotification(
                            'success',
                            `${success.length} ${plural(success.length, 'worksheet', 'worksheets')} marked as reviewed!`
                        );
                    }

                    toastNotification('error', `Unable to review ${errors.length} ${plural(errors.length, 'worksheet', 'worksheets')}!`);

                    const errorMessages: { [id: string]: string } = {};

                    for (const err of errors) {
                        errorMessages[err.error.fields.id] = err.error.fields.errorMessage;
                    }

                    onError(errorMessages);
                    onClose();
                },
            }
        );
    }

    return (
        <Dialog
            isOpen
            title={`Review ${plural(worksheetIds.length, 'worksheet', 'worksheets')}`}
            type="info"
            description={`${descriptionStart} Please ensure that you've checked each worksheet to verify they are correct.`}
            primaryAction="Review"
            secondaryAction="Cancel"
            onPrimaryActionClick={review}
            onClose={onClose}
            onSecondaryActionClick={onClose}
            isLoading={reviewWorksheetsQuery.isLoading}
        />
    );
}
export default WorksheetReviewConfirm;
